import { type ReactNode, type MouseEvent } from 'react';
import {
  ChevronRight,
  Database,
  Table,
  Eye,
  Braces,
  Folder,
  Server,
  KeyRound,
  Link2,
  Hash,
  FileCode2,
  Bookmark,
  Users,
} from 'lucide-react';
import clsx from 'clsx';

export type TreeNodeIcon =
  | 'connection'
  | 'database'
  | 'schema'
  | 'folder'
  | 'table'
  | 'view'
  | 'function'
  | 'column-pk'
  | 'column-fk'
  | 'column'
  | 'saved-queries-folder'
  | 'saved-query';

interface TreeNodeProps {
  label: string;
  depth: number;
  icon: TreeNodeIcon;
  expanded?: boolean;
  hasChildren?: boolean;
  isConnecting?: boolean;
  statusDot?: 'connected' | 'disconnected' | 'error' | 'connecting';
  badge?: string | number;
  /** Show a small "shared with you" people icon to the right of the label.
   *  Used on connections + tunnels owned by another user. */
  sharedBadge?: boolean;
  /** Optional accent color (CSS color) for the row's left border stripe.
   *  Used for connection/tunnel rows that carry a colorTag so users can
   *  tell prod vs staging at a glance — even with identical names. */
  accentColor?: string | null;
  /** Current sidebar search term. When non-empty, matching substrings in the
   *  label are wrapped with a highlight so the user can spot why this row
   *  showed up. Case-insensitive. */
  highlight?: string;
  /** Stable tree-position key, written as `data-tree-key` on the row. Used by
   *  "Expand to active tab" to find the right DOM element and scrollIntoView.
   *  Matches the same key shape used in `treeExpanded` (conn:, db:, schema:,
   *  tables:, table:, view:, function:). */
  treeKey?: string;
  onClick?: () => void;
  onDoubleClick?: () => void;
  onToggle?: () => void;
  onContextMenu?: (e: MouseEvent<HTMLDivElement>) => void;
}

/**
 * Split `text` around case-insensitive occurrences of `term` so the matching
 * runs can be rendered with a highlight. Returns the original text as a
 * single node when `term` is empty or absent.
 */
function renderHighlighted(text: string, term: string | undefined): ReactNode {
  if (!term) return text;
  const lower = text.toLowerCase();
  const needle = term.toLowerCase();
  if (!lower.includes(needle)) return text;
  const parts: ReactNode[] = [];
  let cursor = 0;
  while (cursor < text.length) {
    const idx = lower.indexOf(needle, cursor);
    if (idx === -1) {
      parts.push(text.slice(cursor));
      break;
    }
    if (idx > cursor) parts.push(text.slice(cursor, idx));
    parts.push(
      <mark
        key={idx}
        className="bg-accent-yellow/30 text-text-primary rounded-sm px-[1px]"
      >
        {text.slice(idx, idx + needle.length)}
      </mark>,
    );
    cursor = idx + needle.length;
  }
  return <>{parts}</>;
}

const ICON_MAP: Record<TreeNodeIcon, ReactNode> = {
  connection: <Server size={16} />,
  database: <Database size={16} />,
  schema: <Database size={16} />,
  folder: <Folder size={16} />,
  table: <Table size={16} />,
  view: <Eye size={16} />,
  function: <Braces size={16} />,
  'column-pk': <KeyRound size={16} />,
  'column-fk': <Link2 size={16} />,
  column: <Hash size={16} />,
  'saved-queries-folder': <Bookmark size={16} />,
  'saved-query': <FileCode2 size={16} />,
};

const ICON_COLOR_MAP: Record<TreeNodeIcon, string> = {
  connection: 'text-text-secondary',
  database: 'text-accent-blue',
  schema: 'text-accent-purple',
  folder: 'text-accent-yellow',
  table: 'text-accent-blue',
  view: 'text-accent-teal',
  function: 'text-accent-orange',
  'column-pk': 'text-accent-yellow',
  'column-fk': 'text-accent-blue',
  column: 'text-text-muted',
  'saved-queries-folder': 'text-accent-blue',
  'saved-query': 'text-accent-blue',
};

export function TreeNode({
  label,
  depth,
  icon,
  expanded = false,
  hasChildren = false,
  isConnecting = false,
  statusDot,
  badge,
  sharedBadge,
  accentColor,
  highlight,
  treeKey,
  onClick,
  onDoubleClick,
  onToggle,
  onContextMenu,
}: TreeNodeProps) {
  const handleChevronClick = (e: MouseEvent) => {
    e.stopPropagation();
    onToggle?.();
  };

  const handleClick = () => {
    onClick?.();
  };

  return (
    <div
      data-tree-key={treeKey}
      className="relative flex items-center py-[3px] pr-2 cursor-pointer hover:bg-dbgo-tab-hover transition-colors group select-none"
      style={{
        paddingLeft: depth * 12 + 4,
        // 3px left stripe for rows that carry a colorTag (connection / tunnel).
        // Inset BoxShadow rather than borderLeft so the stripe doesn't shift
        // the row's left padding relative to sibling rows without a color.
        boxShadow: accentColor ? `inset 3px 0 0 0 ${accentColor}` : undefined,
      }}
      onClick={handleClick}
      onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick?.(); }}
      onContextMenu={onContextMenu}
    >
      {/* Chevron */}
      <div
        className="w-4 h-4 flex items-center justify-center flex-shrink-0"
        onClick={handleChevronClick}
      >
        {hasChildren && (
          <ChevronRight
            size={16}
            className={clsx(
              'text-text-muted transition-transform duration-150',
              expanded && 'rotate-90',
            )}
          />
        )}
      </div>

      {/* Status dot (for connections) */}
      {statusDot && (
        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mr-0.5">
          <div
            className={clsx(
              'w-[7px] h-[7px] rounded-full',
              statusDot === 'connected' && 'bg-accent-green shadow-[0_0_6px_#3dd68c]',
              statusDot === 'disconnected' && 'bg-text-muted',
              statusDot === 'error' && 'bg-accent-red',
              statusDot === 'connecting' && 'bg-accent-yellow animate-pulse',
            )}
          />
        </div>
      )}

      {/* Icon */}
      <div className={clsx('w-4 h-4 flex items-center justify-center flex-shrink-0 mr-1.5', ICON_COLOR_MAP[icon])}>
        {ICON_MAP[icon]}
      </div>

      {/* Label */}
      <span
        className={clsx(
          'text-[12.5px] truncate flex-1 min-w-0',
          isConnecting ? 'text-text-muted italic' : 'text-text-primary',
        )}
        title={label}
      >
        {isConnecting ? 'Connecting...' : renderHighlighted(label, highlight)}
      </span>

      {/* Shared-with-you badge — small people icon. Sits before the count
          badge so the count stays right-aligned. */}
      {sharedBadge && (
        <Users
          size={12}
          className="ml-1.5 text-text-muted flex-shrink-0"
          aria-label="Shared with you"
        >
          <title>Shared with you</title>
        </Users>
      )}

      {/* Badge */}
      {badge !== undefined && badge !== null && (
        <span className="ml-1.5 text-[10px] font-mono text-text-muted bg-dbgo-badge px-1.5 py-[1px] rounded-sm flex-shrink-0">
          {typeof badge === 'number' ? formatCount(badge) : badge}
        </span>
      )}
    </div>
  );
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
